/**
 * Что прочиталось из файла импорта реестра или выписки — сразу после выбора файла, до
 * отправки на сервер: имя файла, формат и сколько в нём документов или строк. Если
 * кириллица 1С-файла прочиталась не в той кодировке, ключи вида «СекцияДокумент» не
 * находятся — это видно по нулю, а не по «успешно, 0 заявок». Модуль без импортов
 * значений — проверяется node:test (`importFile.test.mjs`).
 */

export type ImportFormat = 'auto' | '1c_client_bank' | 'tsv' | 'json';

export interface ImportFileInfo {
  format: ImportFormat;
  /** Документов 1С, строк таблицы без заголовка или записей JSON; `null` — не посчитать. */
  count: number | null;
  /** Строка для экрана. */
  text: string;
}

/** Платёжных документов (`СекцияДокумент=…`) в тексте файла 1С — как считает сервер. */
export function count1cDocuments(text: string): number {
  return text.split(/\r?\n/).filter((l) => l.trimStart().startsWith('СекцияДокумент')).length;
}

export const NO_1C_DOCUMENTS =
  'В файле не найдено ни одного платёжного документа. Проверьте, что это выгрузка из 1С ' +
  '(формат 1CClientBankExchange)';

export function describeImportFile(name: string, text: string): ImportFileInfo {
  const trimmed = text.trim();
  if (trimmed.includes('1CClientBankExchange')) {
    const count = count1cDocuments(trimmed);
    return {
      format: '1c_client_bank',
      count,
      text:
        count > 0
          ? `Файл «${name}»: выгрузка 1С, платёжных документов — ${count}`
          : `Файл «${name}»: ${NO_1C_DOCUMENTS}`,
    };
  }
  if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
    let count: number | null = null;
    try {
      const parsed: unknown = JSON.parse(trimmed);
      if (Array.isArray(parsed)) count = parsed.length;
      else if (parsed && typeof parsed === 'object') {
        const obj = parsed as { requests?: unknown; invoices?: unknown };
        const list = Array.isArray(obj.requests) ? obj.requests : obj.invoices;
        count = Array.isArray(list) ? list.length : 1;
      }
    } catch {
      return { format: 'json', count: null, text: `Файл «${name}»: JSON не читается — проверьте файл` };
    }
    return { format: 'json', count, text: `Файл «${name}»: JSON, записей — ${count ?? 0}` };
  }
  const rows = trimmed.split(/\r?\n/).filter((l) => l.trim() !== '');
  const count = Math.max(0, rows.length - 1);
  return {
    format: rows[0]?.includes('\t') ? 'tsv' : 'auto',
    count,
    text: `Файл «${name}»: таблица, строк без заголовка — ${count}`,
  };
}
